import React from 'react';
import { Header } from '../components/Header';
import { Footer } from '../components/Footer';
import { Target, Microscope, Users, BookOpen, Compass, Award, Globe, CheckCircle, Beaker, ArrowRight, GraduationCap } from 'lucide-react';
export function About() {
  const leadershipTeam = [{
    name: 'Dr. Michael Patel',
    title: 'Executive Director',
    bio: 'Dr. Patel leads CalMiR’s organization and strategy with expertise in clinical microbiology and immunology. Previously, he directed clinical trials at leading pharmaceutical companies and academic medical centers.',
    image: 'https://images.unsplash.com/photo-1560250097-0b93528c311a?ixlib=rb-4.0.3&ixid=M3wxMjA3fDB8MHxwaG90by1wYWdlfHx8fGVufDB8fHx8fA%3D%3D&auto=format&fit=crop&w=774&q=80'
  }, {
    name: 'Azn Alahah',
    title: 'Chief R&D Officer',
    bio: 'Azn Alahah is a microbiome scientist with over 5 years of experience in pharmaceutical research and wellness science. He leads CalMiR’s research and development agenda, bridging the gap between academic research and real-world health applications.',
    image: 'https://uploadthingy.s3.us-west-1.amazonaws.com/ehAQ8Bo9qatnBDWC5pLTdX/kokk.jpg'
  }, {
    name: 'Dr. Amara Okafor',
    title: 'Director, Global Health Initiatives',
    bio: 'Dr. Okafor oversees our Afya Program, bringing extensive experience in global health and infectious disease. She has implemented health programs across three continents with major international organizations.',
    image: 'https://images.unsplash.com/photo-1551836022-d5d88e9218df?ixlib=rb-4.0.3&ixid=M3wxMjA3fDB8MHxwaG90by1wYWdlfHx8fGVufDB8fHx8fA%3D%3D&auto=format&fit=crop&w=774&q=80'
  }, {
    name: 'Dr. James Rodriguez',
    title: 'Director, Ukweli Certification',
    bio: 'Dr. Rodriguez leads our certification program with a background in analytical chemistry and supplement regulation. He previously served as an advisor to regulatory agencies on quality standards.',
    image: 'https://images.unsplash.com/photo-1472099645785-5658abf4ff4e?ixlib=rb-4.0.3&ixid=M3wxMjA3fDB8MHxwaG90by1wYWdlfHx8fGVufDB8fHx8fA%3D%3D&auto=format&fit=crop&w=1770&q=80'
  }];
  const advisoryBoard = [{
    name: 'Professor Jennifer Wilson, PhD',
    affiliation: 'Stanford University',
    expertise: 'Microbiome Genomics',
    bio: 'Leading researcher in microbiome sequencing and computational analysis with over 100 publications in top-tier journals.'
  }, {
    name: 'Dr. Robert Tanaka, MD',
    affiliation: 'Harvard Medical School',
    expertise: 'Gastroenterology',
    bio: 'Clinical researcher specializing in gut-brain axis and inflammatory bowel conditions with 20+ years of clinical practice.'
  }, {
    name: 'Dr. Maria Gonzalez, PhD',
    affiliation: 'MIT',
    expertise: 'Bioinformatics',
    bio: 'Pioneer in applying machine learning to microbiome data analysis and personalized intervention prediction.'
  }, {
    name: 'Dr. Kwame Nkrumah, MD, MPH',
    affiliation: 'University of Cape Town',
    expertise: 'Global Health',
    bio: 'Expert in adapting health interventions for resource-limited settings and implementing community-based health programs.'
  }, {
    name: 'Dr. Lisa Chen, PharmD',
    affiliation: 'UC San Francisco',
    expertise: 'Pharmaceutical Science',
    bio: 'Specialist in drug-microbiome interactions and formulation of targeted microbiome therapeutics.'
  }, {
    name: 'Professor David Singh, PhD',
    affiliation: 'Oxford University',
    expertise: 'Immunology',
    bio: 'Researcher focused on microbiome influences on immune system development and function across the lifespan.'
  }];
  const coreValues = [{
    title: 'Scientific Integrity',
    description: 'We uphold the highest standards of research rigor, transparency, and reproducibility in all our work.',
    icon: <Microscope className="text-teal-600" size={24} data-id="element-242" data-mp="112xtywhqgzu3" />
  }, {
    title: 'Truth-Driven Innovation',
    description: "We let scientific evidence guide our innovations, separating what works from what doesn't.",
    icon: <Compass className="text-teal-600" size={24} data-id="element-243" data-mp="r3q8tn4rc0wf" />
  }, {
    title: 'Global Equity',
    description: 'We believe microbiome science should benefit all communities, regardless of geography or economic status.',
    icon: <Globe className="text-teal-600" size={24} data-id="element-244" data-mp="1ou3919funo3c" />
  }, {
    title: 'Translational Impact',
    description: 'We bridge the gap between academic discoveries and real-world health solutions.',
    icon: <BookOpen className="text-teal-600" size={24} data-id="element-245" data-mp="1eucgveerhl6f" />
  }, {
    title: 'Collaborative Spirit',
    description: 'We foster partnerships across disciplines, sectors, and borders to accelerate progress.',
    icon: <Users className="text-teal-600" size={24} data-id="element-246" data-mp="3hjrubdcvau2" />
  }, {
    title: 'Consumer Empowerment',
    description: 'We provide clear, evidence-based information that empowers informed health decisions.',
    icon: <Award className="text-teal-600" size={24} data-id="element-247" data-mp="3n5dafbsihmga" />
  }];
  const ecosystemParts = [{
    name: 'CalMiR',
    description: 'Our flagship research institute conducting basic science, applied research, and clinical trials to establish academic credibility and scientific validation.',
    icon: <Microscope className="text-teal-600" size={24} data-id="element-248" data-mp="34crfqutcc1xa" />,
    color: 'bg-teal-100',
    link: '/research'
  }, {
    name: 'CalBiome',
    description: 'Our product innovation division that develops and refines microbiota reset protocols and therapeutic formulations based on our research findings.',
    icon: <Beaker className="text-blue-600" size={24} data-id="element-249" data-mp="2qk2udmljs6ds" />,
    color: 'bg-blue-100',
    link: '/calbiome'
  }, {
    name: 'Ukweli Institute',
    description: 'Our independent certification body that validates supplements and wellness products against rigorous, evidence-based standards.',
    icon: <CheckCircle className="text-emerald-600" size={24} data-id="element-250" data-mp="3faftg7uxmqdn" />,
    color: 'bg-emerald-100',
    link: '/certification'
  }, {
    name: 'Afya Program',
    description: 'Our global health initiative that adapts microbiome interventions for underserved communities worldwide, promoting health equity.',
    icon: <Globe className="text-indigo-600" size={24} data-id="element-251" data-mp="2sde1ge07r6r1" />,
    color: 'bg-indigo-100',
    link: '/about/afya-program'
  }];
  return <div className="flex flex-col min-h-screen bg-white" data-id="element-252" data-mp="31bg6d4n2ijjl">
      <Header data-id="element-253" data-mp="31e2ep6hs932v" />
      <main className="flex-grow" data-id="element-254" data-mp="36b2ljjmgi9v1">
        {/* Hero Section */}
        <section className="relative w-full bg-gray-50" data-id="element-255" data-mp="36cdnddwcd7ji">
          <div className="absolute inset-0 overflow-hidden" data-id="element-256" data-mp="gma37qlf8pwh">
            <div className="absolute inset-0 bg-cover bg-center opacity-20" style={{
            backgroundImage: "url('https://images.unsplash.com/photo-1588681664899-f142ff2dc9b1?ixlib=rb-4.0.3&ixid=M3wxMjA3fDB8MHxwaG90by1wYWdlfHx8fGVufDB8fHx8fA%3D%3D&auto=format&fit=crop&w=2074&q=80')",
            backgroundBlendMode: 'overlay'
          }} data-id="element-257" data-mp="go4lex5b63wv" />
            <div className="absolute inset-0 bg-gradient-to-r from-teal-600/20 to-blue-600/20" data-id="element-258" data-mp="1re9czh4grejw" />
          </div>
          <div className="container mx-auto px-4 py-16 md:py-24 relative z-10" data-id="element-259" data-mp="1ncxndbllatbp">
            <div className="max-w-3xl" data-id="element-260" data-mp="1idbax4dl5h3y">
              <h1 className="text-3xl md:text-5xl font-bold text-gray-800 mb-4" data-id="element-261" data-mp="1ici9mixebjt7">
                About CalMiR
              </h1>
              <div className="w-20 h-1 bg-teal-600 mb-6" data-id="element-262" data-mp="1dgb1cjhj1w83"></div>
              <p className="text-lg md:text-xl text-gray-700 mb-8" data-id="element-263" data-mp="18g6ttiylkbu0">
                The California Institute for Microbiome Research is an
                independent, science-first organization dedicated to advancing
                truth in microbiome science and wellness interventions.
              </p>
            </div>
          </div>
        </section>
        {/* Our Story Section */}
        <section className="py-16 bg-white" data-id="element-264" data-mp="2gn5bbrw85y9o">
          <div className="container mx-auto px-4" data-id="element-265" data-mp="2gozvv53l3od8">
            <div className="max-w-3xl mx-auto text-center mb-12" data-id="element-266" data-mp="22wa9gzk58hfl">
              <h2 className="text-3xl font-bold text-gray-800 mb-4" data-id="element-267" data-mp="26x2ipst0mmbv">
                Our Story
              </h2>
              <div className="w-20 h-1 bg-teal-600 mx-auto mb-6" data-id="element-268" data-mp="1sxkt5b7wb51g"></div>
            </div>
            <div className="grid md:grid-cols-2 gap-12" data-id="element-269" data-mp="1xwe2crg5g9vt">
              <div data-id="element-270" data-mp="1xxyz8npxs4wu">
                <h3 className="text-xl font-bold text-gray-800 mb-4" data-id="element-271" data-mp="1xub81v7yrula">
                  Bridging Science and Wellness
                </h3>
                <p className="text-gray-600 mb-6" data-id="element-272" data-mp="1nxood9rdj9ku">
                  CalMiR was founded in 2024 by Azn Alahah, who recognized a
                  critical gap in the wellness industry: the lack of
                  pharmaceutical-grade research rigor applied to microbiome
                  science and consumer health products.
                </p>
                <p className="text-gray-600 mb-6" data-id="element-273" data-mp="16p5na1rxru87">
                  After years in pharmaceutical research, Azn Alahah saw how
                  emerging discoveries about the microbiome were being rapidly
                  commercialized without adequate scientific validation,
                  creating a market flooded with products of varying quality and
                  effectiveness.
                </p>
                <p className="text-gray-600" data-id="element-274" data-mp="1qeps714wkiay">
                  He established CalMiR as an independent research institute
                  that would stand apart from commercial interests, applying the
                  highest standards of scientific inquiry to validate what
                  works, debunk what doesn't, and ensure that microbiome science
                  benefits all communities.
                </p>
              </div>
              <div data-id="element-275" data-mp="18m77mw8cr7">
                <h3 className="text-xl font-bold text-gray-800 mb-4" data-id="element-276" data-mp="209lv6c746e">
                  Growing a Global Impact
                </h3>
                <p className="text-gray-600 mb-6" data-id="element-277" data-mp="3cbmwczjjorpo">
                  What began as a small laboratory has grown into a
                  comprehensive ecosystem that spans basic science, product
                  development, independent certification, and global health
                  initiatives.
                </p>
                <p className="text-gray-600 mb-6" data-id="element-278" data-mp="3v48yg1l1peip">
                  Today, CalMiR collaborates with academic institutions,
                  healthcare providers, industry partners, and non-profit
                  organizations around the world. Our research has been
                  published in leading scientific journals, and our
                  certification standards are increasingly recognized as the
                  gold standard for microbiome product quality.
                </p>
                <p className="text-gray-600" data-id="element-279" data-mp="dwnalkwzla1a">
                  Through our Afya Program, we're extending the benefits of
                  microbiome science to underserved communities worldwide,
                  adapting our protocols to diverse settings and building local
                  capacity for sustainable health improvements.
                </p>
              </div>
            </div>
          </div>
        </section>
        {/* Mission & Vision Section */}
        <section className="py-16 bg-gray-50" data-id="element-280" data-mp="z56u0omn9kq9">
          <div className="container mx-auto px-4" data-id="element-281" data-mp="1475unqr023ji">
            <div className="grid md:grid-cols-2 gap-12" data-id="element-282" data-mp="144jp1wno5y3x">
              <div className="bg-white p-8 rounded-lg shadow-sm hover:shadow-md transition-shadow duration-300" data-id="element-283" data-mp="194fk36ee2f7c">
                <div className="flex items-center mb-4" data-id="element-284" data-mp="1dwpdwximjzew">
                  <div className="bg-teal-100 p-3 rounded-full mr-4" data-id="element-285" data-mp="1ivs9zsgmmvaq">
                    <Target className="text-teal-600" size={24} data-id="element-286" data-mp="1ix3btmqihsz7" />
                  </div>
                  <h3 className="text-xl font-bold text-gray-800" data-id="element-287" data-mp="1musqv5fjnnv3">
                    Our Mission
                  </h3>
                </div>
                <p className="text-gray-600 mb-4" data-id="element-288" data-mp="3nyld7hu0754m">
                  To bridge the gap between biohacker discoveries, consumer
                  wellness products, and pharma-level science — by conducting
                  credible, reproducible studies that empower consumers,
                  clinicians, and policymakers with truth-driven health
                  innovation.
                </p>
                <p className="text-gray-600" data-id="element-289" data-mp="bgx02bsos4g4">
                  We apply pharmaceutical-grade research rigor to microbiome and
                  wellness science—validating what works, debunking what
                  doesn't, and ensuring global communities benefit from
                  truth-driven health innovation.
                </p>
              </div>
              <div className="bg-white p-8 rounded-lg shadow-sm hover:shadow-md transition-shadow duration-300" data-id="element-290" data-mp="fhgx7fd9f829">
                <div className="flex items-center mb-4" data-id="element-291" data-mp="2haociosvhpo5">
                  <div className="bg-teal-100 p-3 rounded-full mr-4" data-id="element-292" data-mp="2hc7rmc2sluoc">
                    <Compass className="text-teal-600" size={24} data-id="element-293" data-mp="wscgn1mdazsh" />
                  </div>
                  <h3 className="text-xl font-bold text-gray-800" data-id="element-294" data-mp="11s8l11yz8t8k">
                    Our Vision
                  </h3>
                </div>
                <p className="text-gray-600 mb-4" data-id="element-295" data-mp="o022xac9kc6h">
                  We envision a future where microbiome science fulfills its
                  promise as a transformative force in human health—where
                  evidence-based interventions are accessible to all, where
                  consumers can trust the products they use, and where the
                  wellness industry is held to the same standards of scientific
                  rigor as pharmaceutical medicine.
                </p>
                <p className="text-gray-600" data-id="element-296" data-mp="93b9dhp9tuos">
                  By 2030, we aim to establish a new paradigm for wellness
                  science that eliminates the false dichotomy between "natural"
                  and "pharmaceutical" approaches, replacing it with a unified
                  standard of evidence that puts human health outcomes first.
                </p>
              </div>
            </div>
          </div>
        </section>
        {/* Core Values Section */}
        <section className="py-16 bg-white" data-id="element-297" data-mp="14qnsytu714t2">
          <div className="container mx-auto px-4" data-id="element-298" data-mp="zt5r1nelfpuk">
            <div className="max-w-3xl mx-auto text-center mb-12" data-id="element-299" data-mp="zsmbgdcf1djp">
              <h2 className="text-3xl font-bold text-gray-800 mb-4" data-id="element-300" data-mp="vs2b70wjpttc">
                Our Core Values
              </h2>
              <div className="w-20 h-1 bg-teal-600 mx-auto mb-6" data-id="element-301" data-mp="r0284zl6iksj"></div>
              <p className="text-gray-600" data-id="element-302" data-mp="m17qw22v14aj">
                These principles guide everything we do, from the design of our
                research studies to our global health partnerships and
                certification standards.
              </p>
            </div>
            <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6" data-id="element-303" data-mp="c5e4lqzc9wfk">
              {coreValues.map((value, index) => <div key={index} className="bg-gray-50 p-6 rounded-lg shadow-sm hover:shadow-md transition-shadow duration-300" data-id="element-304" data-mp="2jpl9pqh4blaa" data-mpi={index}>
                  <div className="bg-teal-100 p-3 rounded-full w-12 h-12 flex items-center justify-center mb-4" data-id="element-305" data-mp="2sq00348xvqkn">
                    {value.icon}
                  </div>
                  <h3 className="text-xl font-bold text-gray-800 mb-2" data-id="element-306" data-mp="2xob8rbnhzv1a">
                    {value.title}
                  </h3>
                  <p className="text-gray-600" data-id="element-307" data-mp="250f5fo3fb4vj">{value.description}</p>
                </div>)}
            </div>
          </div>
        </section>
        {/* Leadership Team Section */}
        <section className="py-16 bg-gray-50" data-id="element-308" data-mp="2eq8eoig3f7wm">
          <div className="container mx-auto px-4" data-id="element-309" data-mp="2er0btxpc9dp0">
            <div className="max-w-3xl mx-auto text-center mb-12" data-id="element-310" data-mp="1haukx0rmfzk5">
              <h2 className="text-3xl font-bold text-gray-800 mb-4" data-id="element-311" data-mp="1hakrt4ba5ca8">
                Our Leadership Team
              </h2>
              <div className="w-20 h-1 bg-teal-600 mx-auto mb-6" data-id="element-312" data-mp="1m7kzfjns2n3g"></div>
              <p className="text-gray-600" data-id="element-313" data-mp="1e7onkfi2gsrt">
                Meet the experts who guide our research and initiatives,
                bringing decades of combined experience in microbiome science,
                pharmaceutical research, and global health.
              </p>
            </div>
            <div className="grid md:grid-cols-2 gap-8" data-id="element-314" data-mp="1j0hvfc8u0h84">
              {leadershipTeam.map((leader, index) => <div key={index} className="bg-white rounded-lg shadow-sm overflow-hidden hover:shadow-md transition-shadow duration-300" data-id="element-315" data-mp="vanikuoiisnv" data-mpi={index}>
                  <div className="flex flex-col md:flex-row" data-id="element-316" data-mp="15a4kd10n8mek">
                    <div className="md:w-1/3" data-id="element-317" data-mp="109762zir8cic">
                      <img src={leader.image} alt={leader.name} className="w-full h-full object-cover aspect-square" data-id="element-318" data-mp="10bjlp611ias8" />
                    </div>
                    <div className="md:w-2/3 p-6" data-id="element-319" data-mp="3tlquswzywnxj">
                      <h3 className="text-xl font-bold text-gray-800 mb-1" data-id="element-320" data-mp="3tkxlbo9mjkbp">
                        {leader.name}
                      </h3>
                      <p className="text-teal-600 font-medium mb-3" data-id="element-321" data-mp="3klu0ofut6wv3">
                        {leader.title}
                      </p>
                      <p className="text-gray-600" data-id="element-322" data-mp="3plgdio6q6b3d">{leader.bio}</p>
                    </div>
                  </div>
                </div>)}
            </div>
          </div>
        </section>
        {/* Advisory Board Section */}
        <section className="py-16 bg-white" data-id="element-323" data-mp="l89tink0f8pu">
          <div className="container mx-auto px-4" data-id="element-324" data-mp="7fttzwqbhsto">
            <div className="max-w-3xl mx-auto text-center mb-12" data-id="element-325" data-mp="7d7py72mxvg7">
              <h2 className="text-3xl font-bold text-gray-800 mb-4" data-id="element-326" data-mp="cckceqplh0i9">
                Scientific Advisory Board
              </h2>
              <div className="w-20 h-1 bg-teal-600 mx-auto mb-6" data-id="element-327" data-mp="101v4ljsvpmks"></div>
              <p className="text-gray-600" data-id="element-328" data-mp="14vzdi8lpg4oi">
                Our work is guided by a distinguished advisory board of leading
                experts in microbiome science, medicine, bioinformatics, and
                global health.
              </p>
            </div>
            <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6" data-id="element-329" data-mp="2r6zr8dj81bff">
              {advisoryBoard.map((advisor, index) => <div key={index} className="bg-gray-50 p-6 rounded-lg shadow-sm hover:shadow-md transition-shadow duration-300" data-id="element-330" data-mp="2ma9cbtj5klvl" data-mpi={index}>
                  <div className="flex items-start mb-4" data-id="element-331" data-mp="3eydlip0xnh31">
                    <div className="bg-teal-100 p-3 rounded-full mr-4 flex-shrink-0" data-id="element-332" data-mp="39y8z6idcfs4w">
                      <GraduationCap className="text-teal-600" size={20} data-id="element-333" data-mp="3a03n85jww0d7" />
                    </div>
                    <div data-id="element-334" data-mp="35z06lgu1i0ax">
                      <h3 className="text-lg font-bold text-gray-800 mb-1" data-id="element-335" data-mp="1yjlqe4cejvq1">
                        {advisor.name}
                      </h3>
                      <p className="text-gray-600 text-sm mb-1" data-id="element-336" data-mp="1ufxj7mron25b">
                        {advisor.affiliation}
                      </p>
                      <p className="text-teal-600 text-sm font-medium mb-3" data-id="element-337" data-mp="e11g2ku4lsh2">
                        {advisor.expertise}
                      </p>
                    </div>
                  </div>
                  <p className="text-gray-600 text-sm" data-id="element-338" data-mp="iybgevj1zojo">{advisor.bio}</p>
                </div>)}
            </div>
          </div>
        </section>
        {/* The CalMiR Ecosystem */}
        <section className="py-16 bg-gray-50" data-id="element-339" data-mp="ssaar8cvy412">
          <div className="container mx-auto px-4" data-id="element-340" data-mp="xnzhbvnbol6v">
            <div className="max-w-3xl mx-auto text-center mb-12" data-id="element-341" data-mp="xqvf9jz679vb">
              <h2 className="text-3xl font-bold text-gray-800 mb-4" data-id="element-342" data-mp="11r5hqmnr7i5f">
                The CalMiR Ecosystem
              </h2>
              <div className="w-20 h-1 bg-teal-600 mx-auto mb-6" data-id="element-343" data-mp="16ncrkqj9tdsn"></div>
              <p className="text-gray-600" data-id="element-344" data-mp="33jjxpg6qmcj7">
                CalMiR operates at the center of a four-part ecosystem, creating
                a closed loop of science → validation → productization →
                adoption.
              </p>
            </div>
            <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6" data-id="element-345" data-mp="2x8wyd3sfizbc">
              {ecosystemParts.map((part, index) => <div key={index} className="bg-white p-6 rounded-lg shadow-sm hover:shadow-md transition-shadow duration-300" data-id="element-346" data-mp="327rfm1ar0ftc" data-mpi={index}>
                  <div className={`${part.color} p-3 rounded-full w-12 h-12 flex items-center justify-center mb-4`} data-id="element-347" data-mp="3b1mmpevfkgbm">
                    {part.icon}
                  </div>
                  <h3 className="text-xl font-bold text-gray-800 mb-2" data-id="element-348" data-mp="3l02fdma3vxn8">
                    <a href={part.link} className="hover:text-teal-600 transition-colors" data-id="element-349" data-mp="3pynfzmt9sjc7">
                      {part.name}
                    </a>
                  </h3>
                  <p className="text-gray-600 mb-4" data-id="element-350" data-mp="1vkvyk14p8v67">{part.description}</p>
                  <a href={part.link} className="inline-flex items-center text-teal-600 hover:text-teal-700 transition-colors duration-200" data-id="element-351" data-mp="cp87heycthbw">
                    Learn more
                    <ArrowRight size={16} className="ml-1 transform group-hover:translate-x-1 transition-transform duration-200" data-id="element-352" data-mp="3x68ll3xo93q" />
                  </a>
                </div>)}
            </div>
            <div className="mt-12 max-w-3xl mx-auto text-center" data-id="element-353" data-mp="rlzb60f29f7b">
              <p className="text-gray-700 italic" data-id="element-354" data-mp="mn5trwwnl41v">
                "Together, this creates a closed loop of science → validation →
                productization → adoption."
              </p>
            </div>
          </div>
        </section>
        {/* Join Us Section */}
        <section className="py-16 bg-teal-600 text-white" data-id="element-355" data-mp="37eoevo425821">
          <div className="container mx-auto px-4" data-id="element-356" data-mp="32d8vs1lfxbma">
            <div className="max-w-3xl mx-auto text-center" data-id="element-357" data-mp="32gmzpe9r5svu">
              <h2 className="text-3xl font-bold mb-4" data-id="element-358" data-mp="33wqz0bhjqeqg">Join Our Mission</h2>
              <p className="text-teal-100 mb-8" data-id="element-359" data-mp="38usjn0umem41">
                Whether you're a researcher, clinician, potential partner, or
                supporter, there are many ways to get involved with CalMiR's
                work.
              </p>
              <div className="flex flex-wrap justify-center gap-4" data-id="element-360" data-mp="2zs16mwm3jiaa">
                <a href="/careers" className="px-6 py-3 bg-white text-teal-600 rounded-md hover:bg-gray-100 transition-colors duration-200 shadow-sm" data-id="element-361" data-mp="2l3wte85tbyyw">
                  Explore Careers
                </a>
                <a href="/contact" className="px-6 py-3 bg-teal-700 text-white border border-teal-400 rounded-md hover:bg-teal-800 transition-colors duration-200 shadow-sm" data-id="element-362" data-mp="2g5v24zy8gtcc">
                  Contact Us
                </a>
                <a href="/support-us" className="px-6 py-3 bg-teal-700 text-white border border-teal-400 rounded-md hover:bg-teal-800 transition-colors duration-200 shadow-sm" data-id="element-363" data-mp="2779ljacbnoe7">
                  Support Our Work
                </a>
              </div>
            </div>
          </div>
        </section>
      </main>
      <Footer data-id="element-364" data-mp="1shgfw5l1o5ei" />
    </div>;
}